import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import ErrorBoundary from './components/common/ErrorBoundary';
import ProtectedRoute from './components/common/ProtectedRoute';
import { mixpanelTrack } from './utils/tracking/mixpanel';
import ScrollToTop from './components/common/ScrollToTop';
import './index.css';

// Lazy load pages for performance
const Intro = lazy(() => import('./components/pages/Intro'));
const TripType = lazy(() => import('./components/pages/TripType'));
const Destination = lazy(() => import('./components/pages/Destination'));
const Assurance = lazy(() => import('./components/pages/Assurance'));
const Dates = lazy(() => import('./components/pages/Dates'));
const Features = lazy(() => import('./components/pages/Features'));
const AddOns = lazy(() => import('./components/pages/AddOns'));
const State = lazy(() => import('./components/pages/State'));
const Travelers = lazy(() => import('./components/pages/Travelers'));
const Calculating = lazy(() => import('./components/pages/Calculating'));
const NotFound = lazy(() => import('./components/pages/NotFound'));

// Loading component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-secondary-100">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-70"></div>
  </div>
);

const PAGE_VIEW_MAP = {
  '/intro':       { eventName: 'View Intro Page LI',        name_of_page: 'Intro' },
  '/trip-type':   { eventName: 'View Trip Type Page LI',    name_of_page: 'Trip Type' },
  '/destination': { eventName: 'View Destinations Page LI', name_of_page: 'Destinations' },
  '/assurance':   { eventName: 'View Assurance Page LI',    name_of_page: 'Assurance' },
  '/dates':       { eventName: 'View Dates Page LI',        name_of_page: 'Dates' },
  '/features':    { eventName: 'View Features Page LI',     name_of_page: 'Features' },
  '/add-ons':     { eventName: 'View Add Ons Page LI',      name_of_page: 'Add Ons' },
  '/state':       { eventName: 'View State Page LI',        name_of_page: 'State' },
  '/travelers':   { eventName: 'View Travelers Page LI',    name_of_page: 'Travelers' },
  '/calculating': { eventName: 'View Calculating Page LI',  name_of_page: 'Calculating' },
};

const RouteTracker = () => {
  const location = useLocation();
  useEffect(() => {
    const entry = PAGE_VIEW_MAP[location.pathname];
    if (entry) {
      mixpanelTrack(entry.eventName, {
        event_type: 'page view',
        name_of_page: entry.name_of_page,
      });
    }
  }, [location.pathname]);
  return null;
};

function App() {
  // Defensive session clearing on version mismatch or data corruption
  React.useEffect(() => {
    try {
      const SCHEMA_VERSION = 'v1.3'; // Increment to force clear
      const actualVersion = sessionStorage.getItem('faye_schema_version');

      if (actualVersion !== SCHEMA_VERSION) {
        console.warn('App Version Mismatch. Clearing storage...');
        sessionStorage.clear();
        sessionStorage.setItem('faye_schema_version', SCHEMA_VERSION);
      }
    } catch (e) {
      console.error('Session clearing error:', e);
    }
  }, []);

  return (
    <BrowserRouter>
      <ScrollToTop />
      <RouteTracker />
      <ErrorBoundary>
        <div className="App min-h-screen bg-secondary-100">
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Navigate to="/intro" replace />} />
              <Route path="/intro" element={<Intro />} />
              <Route path="/trip-type" element={<Navigate to="/destination" replace />} />
              <Route path="/destination" element={<Destination />} />
              <Route path="/assurance" element={<ProtectedRoute requiredFields={['destinations']}><Assurance /></ProtectedRoute>} />
              <Route path="/dates" element={<ProtectedRoute requiredFields={['destinations']}><Dates /></ProtectedRoute>} />
              <Route path="/features" element={<ProtectedRoute requiredFields={['startDate', 'endDate']}><Features /></ProtectedRoute>} />
              <Route path="/add-ons" element={<ProtectedRoute requiredFields={['startDate', 'endDate']}><AddOns /></ProtectedRoute>} />
              <Route path="/state" element={<State />} />
              <Route path="/travelers" element={<ProtectedRoute requiredFields={['state']}><Travelers /></ProtectedRoute>} />
              <Route path="/calculating" element={<ProtectedRoute requiredFields={['travelers']}><Calculating /></ProtectedRoute>} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </div>
      </ErrorBoundary>
    </BrowserRouter>
  );
}

export default App;
